import { FC, useEffect, useRef, useState } from "react";
import { Mention, MentionsInput } from "react-mentions";
import { toJS } from "mobx";
import { Controller } from "react-hook-form";
import * as Yup from "yup";
import { Row, Col } from "antd";
import { observer } from "mobx-react-lite";
import { useHandleForm } from "../../../hooks/useHandleForm";
import {
  AutoComplete,
  FormBlock,
  FormField,
  IButton,
} from "../../../ui-kit";
import smsSettings from "../../../store/smsSettings";
import dictionaryStore from "../../../store/dictionary";

import styles from "./Mentions.module.scss";
import moduleStyles from "../SmsSettings.module.scss";
import { SmsSettingTemplateType } from "../SmsSettingsTemplates";

const CHILD_BUILDINGS_CODE = "ChildrenBuildings";
const HOSPITAL_BUILDINGS_CODE = "HospitalBuildings";
const LIVING_BUILDINGS_CODE = "LivingBuildingsOff";

const schema = Yup.object().shape({
  eventSeverityId: Yup.string().typeError("Выберите тип события для рассылки"),
});

const SmsSettingsTemplateForm: FC<{ record?: SmsSettingTemplateType }> = observer(
  ({ record }) => {
    const { templatesFormIsSending } = smsSettings;
    const mentionsRef = useRef(null);
    const [templateText, setTemplateText] = useState(record.textDesign);

    const [templatesTextExample, setTemplatesTextExample] = useState("");
    const [templatesTextShortExample, setTemplatesTextShortExample] =
      useState("");
    const initialValues = {
      ...toJS(record),
    };
    const initialFocus = {};
    const { register, handleSubmit, watchAllField, control, errors, setValue } =
      useHandleForm(schema, initialValues, initialFocus);

    const templates = dictionaryStore.data["NotificationTemplateItem"].map(
      (item) => {
        const space = "\u00A0".repeat(4);
        return {
          id: item.code,
          title: item.title,
          display: `${space}${item.title?.replaceAll(" ", "\u00A0")}${space}`,
          exampleValue: item.exampleValue,
          textFull: item.textFull.replace("{value}", ""),
          textShort: item.textShort.replace("{value}", ""),
        };
      },
    );

    useEffect(() => {
      setHolderHeight();
    }, [templatesTextExample, templatesTextShortExample]);

    useEffect(() => {
      const arrTemp = templateText
        .split("@")
        .filter((st) => st.trim())
        .map(transformString);

      const templatesFull = arrTemp?.map((item) => {
        const templateFull = findedElement(item);
        if (templateFull) {
          const { textFull, exampleValue } = templateFull;
          return `${textFull} ${exampleValue ?? ""}`;
        }
        return item;
      });

      const templatesShort = arrTemp.map((item) => {
        const templateShort = findedElement(item);
        if (templateShort) {
          const { id, textShort, exampleValue } = templateShort;
          return `${
            id === CHILD_BUILDINGS_CODE ||
            id === HOSPITAL_BUILDINGS_CODE ||
            id === LIVING_BUILDINGS_CODE
              ? ""
              : textShort
          } 
          ${exampleValue ?? ""}
          ${
            id === CHILD_BUILDINGS_CODE ||
            id === HOSPITAL_BUILDINGS_CODE ||
            id === LIVING_BUILDINGS_CODE
              ? textShort
              : ""
          }`;
        }
        return item;
      });

      setTemplatesTextExample(templatesFull.join(", "));
      setTemplatesTextShortExample(templatesShort.join(", "));
    }, [templateText]);

    const findedElement = (element) => {
      return templates.find((el) => el.id === element);
    };

    const transformString = (str) => {
      let result = "";
      let startIndex = 0;
      let endIndex = 0;

      for (let i = 0; i < str.length; i++) {
        if (str[i] === "(") {
          startIndex = i + 1;
        } else if (str[i] === ")") {
          endIndex = i;
          result += str.substring(startIndex, endIndex);
        }
      }

      return result;
    };

    const onSubmit = (values) => {
      smsSettings.saveSmsTemplate(record.id, {
        ...values,
        textDesign: templateText,
      });
    };

    const handleCommentChange = (e) => {
      const value = e.target.value;
      setTemplateText(value);
    };

    const putMention = (e, item) => {
      setTemplateText(`${templateText}@[${item.display}](${item.id}) `);
      const newTextExample = `${templatesTextExample}${item.textFull} ${
        item.exampleValue ? item.exampleValue : ""
      }, `;
      const newTextShortExample = `${templatesTextShortExample}${
        item.id === CHILD_BUILDINGS_CODE ||
        item.id === HOSPITAL_BUILDINGS_CODE ||
        item.id === LIVING_BUILDINGS_CODE
          ? ""
          : item.textShort
      } 
      ${item.exampleValue ? item.exampleValue : ""} 
      ${
        item.id === CHILD_BUILDINGS_CODE ||
        item.id === HOSPITAL_BUILDINGS_CODE ||
        item.id === LIVING_BUILDINGS_CODE
          ? item.textShort
          : ""
      }, `;

      setTemplatesTextExample(newTextExample);
      setTemplatesTextShortExample(newTextShortExample);
    };

    const setHolderHeight = () => {
      const lineHeight = 40;
      const mentionsContainer = mentionsRef.current.querySelector("textarea");

      const mentionsContainerInitialHeight = mentionsContainer.style.height;
      mentionsContainer.style.height = "auto";
      const mentionsContainerInitialHeightRealHeight =
        mentionsContainer.scrollHeight;
      mentionsContainer.style.height = mentionsContainerInitialHeight;

      mentionsRef.current.style.height = `${
        lineHeight *
        Math.ceil(mentionsContainerInitialHeightRealHeight / lineHeight)
      }px`;
    };

    const mapEventSeverities = (options) => {
      return options.map((item) => {
        return { ...item, label: item.name };
      })
    }

    return (
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={moduleStyles.SmsSettingsForm}
      >
        <Row gutter={[15, 20]}>
          <Col span={12}>
            <Row gutter={[0, 20]}>
              <Col span={24}>
                <FormField
                  label="Название шаблона"
                  name="title"
                  register={register}
                  type="text"
                  currentValue={watchAllField.title}
                />
              </Col>
              <Col span={24}>
                <Controller
                  control={control}
                  name={`eventSeverityName`}
                  render={({ field }) => (
                    <AutoComplete
                      field={field}
                      label="Тип события для рассылки"
                      required
                      getData={smsSettings.findSmsIncidentsParamsByName}
                      mapOptions={mapEventSeverities}
                      defaultSearchString=""
                      onSelect={(opt: any) => setValue("eventSeverityId", opt.id)}
                      onChange={(val) => {
                        if (!val) setValue(`eventSeverityId`, null);
                      }}
                    />
                  )}
                />
              </Col>
            </Row>
          </Col>
          <Col span={12}>
            <FormField
              label="Описание шаблона"
              name="description"
              register={register}
              type="textarea"
              currentValue={watchAllField.description}
              className={moduleStyles.textarea}
            />
          </Col>
          <Col span={12}>
            <FormBlock name="Атрибуты для составления шаблона">
              <div className={styles.MentionChooses}>
                {templates.map((item) => (
                  <IButton
                    key={item.id}
                    type="button"
                    onClick={(e) => putMention(e, item)}
                    className={styles.MentionChoosesButton}
                  >
                    {item.title}
                  </IButton>
                ))}
              </div>
            </FormBlock>
          </Col>
          <Col span={12} className={styles.MentionInput}>
            <FormBlock name="Конструктор типа события">
              <div ref={mentionsRef} className={styles.MentionInputHolder}>
                <MentionsInput
                  markup="@[__display__](__id__)"
                  value={templateText}
                  onChange={handleCommentChange}
                >
                  <Mention trigger="@" data={templates} />
                </MentionsInput>
              </div>
            </FormBlock>
          </Col>
          {templatesTextExample.trim() && (
            <Col span={24}>
              <FormBlock name="Пример текста Email-рассылки на основе конструктора">
                <div>{templatesTextExample}</div>
              </FormBlock>
            </Col>
          )}
          {templatesTextShortExample.trim() && (
            <Col span={24}>
              <FormBlock name="Пример текста СМС-рассылки на основе конструктора">
                <div>{templatesTextShortExample}</div>
              </FormBlock>
            </Col>
          )}
          <Col span={24} className={moduleStyles.SmsSettingsFormButton}>
            <IButton disabled={templatesFormIsSending.value}>
              Сохранить шаблон
            </IButton>
          </Col>
        </Row>
      </form>
    );
  },
);

export default SmsSettingsTemplateForm;
